import { PlatformLocation } from '@angular/common';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthSelectors } from '@store/auth';
import { SettingsSelectors } from '@store/settings';
import { IUser } from '@models/user';
import { IOrganisation } from '@models/organisation';
import { IPartner } from '@models/partner';
import { createSentryReplay } from './sentry-replay';
import { isTokenRoute } from './sentry-scrubber';

/** The slice of the Sentry SDK this service drives — a seam so specs can assert calls. */
export interface SentryApi {
    setUser(user: { id: string; email?: string } | null): void;
    setTag(key: string, value: string | undefined): void;
    setContext(name: string, context: Record<string, unknown> | null): void;
    getReplay(): { stop(): Promise<void>; startBuffering(): void } | undefined;
    addReplay(): void;
}

export const SENTRY_API = new InjectionToken<SentryApi>('SENTRY_API', {
    providedIn: 'root',
    factory: (): SentryApi => ({
        setUser: user => Sentry.setUser(user),
        setTag: (key, value) => Sentry.setTag(key, value),
        setContext: (name, context) => Sentry.setContext(name, context),
        getReplay: () => Sentry.getReplay(),
        addReplay: () => Sentry.addIntegration(createSentryReplay())
    })
});

/** Tag names shared with the backend — sub-project 3 filters on them, so they are a contract. */
export const SENTRY_CONTEXT_TAGS = ['organisation', 'impersonating', 'partner_id', 'partner', 'partner_scope'] as const;

/**
 * Keeps the Sentry user and org/partner tags in step with the store for the whole session, and
 * pauses replay on pages whose URL is a credential (share links, password reset).
 */
@Injectable({ providedIn: 'root' })
export class SentryContextService {
    private readonly sentry = inject(SENTRY_API);
    private readonly authSelectors = inject(AuthSelectors);
    private readonly settingsSelectors = inject(SettingsSelectors);
    private readonly router = inject(Router);
    private readonly platformLocation = inject(PlatformLocation);
    private started = false;
    private onTokenRoute = false;

    start(): void {
        if (this.started) {
            return;
        }

        this.started = true;

        combineLatest([
            this.authSelectors.user$,
            this.settingsSelectors.organisation$,
            this.settingsSelectors.selectedPartners$
        ]).subscribe(([user, organisation, partners]) =>
            this.apply(user ?? null, organisation ?? null, partners ?? [])
        );

        this.router.events
            .pipe(
                filter(
                    (event): event is NavigationStart | NavigationEnd =>
                        event instanceof NavigationStart || event instanceof NavigationEnd
                )
            )
            .subscribe(event =>
                this.toggleReplay(event instanceof NavigationEnd ? event.urlAfterRedirects : event.url)
            );
    }

    private apply(user: IUser | null, organisation: IOrganisation | null, partners: IPartner[]): void {
        if (!user) {
            this.sentry.setUser(null);
            SENTRY_CONTEXT_TAGS.forEach(tag => this.sentry.setTag(tag, undefined));
            this.sentry.setContext('partners', null);

            return;
        }

        this.sentry.setUser({ id: String(user.id), email: user.email });
        this.sentry.setTag('organisation', organisation?.slug);
        this.sentry.setTag('impersonating', organisation?.user_impersonating_mode ? 'true' : 'false');

        if (partners.length === 1) {
            this.sentry.setTag('partner_id', String(partners[0].id));
            this.sentry.setTag('partner', partners[0].display_name);
            this.sentry.setTag('partner_scope', 'single');
            this.sentry.setContext('partners', null);

            return;
        }

        this.sentry.setTag('partner_id', undefined);
        this.sentry.setTag('partner', undefined);
        this.sentry.setTag('partner_scope', partners.length > 1 ? 'multi' : 'none');
        this.sentry.setContext('partners', partners.length > 1 ? { ids: partners.map(partner => partner.id) } : null);
    }

    private toggleReplay(url: string): void {
        // The address bar counts too: an invalid share link renders the 404 view without changing
        // it, and that internal redirect must not look like leaving the token page.
        const browserUrl = this.platformLocation.pathname + this.platformLocation.search;
        const tokenRoute = isTokenRoute(url) || isTokenRoute(browserUrl);

        if (tokenRoute && !this.onTokenRoute) {
            void this.sentry.getReplay()?.stop();
        }

        if (!tokenRoute && this.onTokenRoute) {
            // A page loaded on a token route starts without replay (see main.ts); add it on the way out.
            const replay = this.sentry.getReplay();

            if (replay) {
                replay.startBuffering();
            } else {
                this.sentry.addReplay();
            }
        }

        this.onTokenRoute = tokenRoute;
    }
}
